import { FieldPolicy, Reference } from "@apollo/client";

import { Maybe, PageInfo } from "types/generated";

type PaginatedField = {
  edges: Array<unknown>;
  pageInfo: PageInfo;
};

/**
 * Field policy for infinite scroll pagination.
 * Prevents polling from overwriting edges accumulated via fetchMore + updateQuery.
 * Do NOT use for classical page-based pagination where data should be replaced per page.
 */
export const infiniteScrollFieldPolicy = (): FieldPolicy<Maybe<PaginatedField>> => ({
  merge(existing, incoming) {
    // Initial load or cache cleared by refetch - nothing to preserve
    if (!existing || !incoming) return incoming;

    // fetchMore + updateQuery: updateQuery already merged all pages into incoming,
    // so incoming is always >= existing - accept it as the new cache value
    if (incoming.edges.length >= existing.edges.length) {
      return incoming;
    }

    // Poll returned only the first page (e.g. 50 edges) while cache holds
    // accumulated data (e.g. 500 edges) - refresh the first page with fresh data,
    // keep the remaining accumulated edges, and preserve existing pageInfo
    // so endCursor still points to the last loaded page
    return {
      ...incoming,
      edges: [...incoming.edges, ...existing.edges.slice(incoming.edges.length)],
      pageInfo: existing.pageInfo,
    };
  },
});

/**
 * Field policy for infinite scroll pagination over a plain array field (no edges/pageInfo).
 * Prevents polling/refetch from overwriting items accumulated via fetchMore + updateQuery.
 * Do NOT use for classical page-based pagination where data should be replaced per page.
 */
export const infiniteScrollArrayFieldPolicy = (): FieldPolicy<Maybe<Reference[]>> => ({
  merge(existing, incoming) {
    // Initial load or cache cleared by refetch - nothing to preserve
    if (!existing || !incoming) return incoming;

    // fetchMore + updateQuery: updateQuery already merged all pages into incoming,
    // so incoming is always >= existing - accept it as the new cache value
    if (incoming.length >= existing.length) {
      return incoming;
    }

    // Poll/refetch returned only the first page while cache holds accumulated
    // items - refresh the first page with fresh data, keep the remaining accumulated items
    return [...incoming, ...existing.slice(incoming.length)];
  },
});
